<script setup lang="ts">
import { useMonitoringStore } from '@/stores/monitoring.store'
import { computed, ref } from 'vue'
import type { LinkStatus } from '@dronecast/shared'
import DiagnosticPanel from '@/components/DiagnosticPanel.vue'

const store = useMonitoringStore()
const s = computed(() => store.playerState)
const showDetail = ref(false)

function statusColor(status: LinkStatus): string {
  switch (status) {
    case 'connected': return '#28a745'
    case 'unstable': return '#ffc107'
    case 'disconnected': return '#dc3545'
    default: return '#6c757d'
  }
}

function statusLabel(status: LinkStatus): string {
  switch (status) {
    case 'connected': return '원활'
    case 'unstable': return '불안정'
    case 'disconnected': return '끊김'
    default: return '미확인'
  }
}

function linkLineClass(status: LinkStatus): string {
  switch (status) {
    case 'connected': return 'link-line connected'
    case 'unstable': return 'link-line unstable'
    case 'disconnected': return 'link-line disconnected'
    default: return 'link-line unknown'
  }
}

/** Caster↔Server 구간에 대한 안내 메시지 */
const casterCauseHint = computed(() => {
  const st = s.value.casterServerLink.state
  if (st.status === 'unstable') return '⚠ 송출측 불안정 — 영상이 끊길 수 있습니다'
  if (st.status === 'disconnected') return '✕ 송출측 연결 끊김 — 영상 수신 불가'
  return ''
})

</script>

<template>
  <div class="player-status-wrapper">
    <!-- 상단 바 -->
    <div class="status-bar" @click="showDetail = !showDetail">
      <!-- 네트워크 -->
      <span class="network-badge" :class="{ offline: !s.network.online }">
        📡 {{ s.network.online ? '온라인' : '오프라인' }}
      </span>

      <!-- 3-Node 다이어그램 -->
      <div class="node-link">
        <span class="node">Caster</span>
        <span :class="linkLineClass(s.casterServerLink.state.status)">
          <span class="link-dot" :style="{ backgroundColor: statusColor(s.casterServerLink.state.status) }"></span>
          <span class="link-label">{{ statusLabel(s.casterServerLink.state.status) }}</span>
        </span>
        <span class="node">Server</span>
        <span :class="linkLineClass(s.serverPlayerLink.state.status)">
          <span class="link-dot" :style="{ backgroundColor: statusColor(s.serverPlayerLink.state.status) }"></span>
          <span class="link-label">{{ statusLabel(s.serverPlayerLink.state.status) }}</span>
        </span>
        <span class="node self">Player</span>
      </div>

      <span class="toggle-hint">{{ showDetail ? '▲' : '▼' }}</span>
    </div>

    <!-- Caster측 원인 안내 -->
    <div v-if="casterCauseHint" class="cause-message" :class="s.casterServerLink.state.status">
      {{ casterCauseHint }}
    </div>

    <!-- Player 자기 Link 원인 메시지 -->
    <div v-if="s.serverPlayerLink.state.cause" class="cause-message" :class="s.serverPlayerLink.state.status">
      {{ s.serverPlayerLink.state.status === 'unstable' ? '⚠' : '✕' }}
      {{ s.serverPlayerLink.state.causeMessage }}
    </div>

    <!-- 상세 패널 (Server↔Player Link — 자기 연결) -->
    <div v-if="showDetail" class="detail-panel">
      <div class="detail-title">내 연결 상세 (Server ↔ Player)</div>
      <DiagnosticPanel role="player" :detail="s.serverPlayerLink.detail" />
    </div>
  </div>
</template>

<style scoped>
.player-status-wrapper {
  margin-bottom: 8px;
  font-size: 12px;
}

.status-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
}

.network-badge {
  font-size: 11px;
  color: #495057;
  white-space: nowrap;
}
.network-badge.offline {
  color: #dc3545;
  font-weight: 600;
}

.node-link {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
}

.node {
  padding: 3px 10px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}
.node.self {
  border-color: #4a90d9;
  color: #4a90d9;
}

.link-line {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  position: relative;
}
.link-line::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 0;
}
.link-line.connected::before { background: #28a745; }
.link-line.unstable::before { background: none; border-top: 2px dashed #ffc107; height: 0; }
.link-line.disconnected::before { background: none; border-top: 2px dashed #dc3545; height: 0; }
.link-line.unknown::before { background: none; border-top: 2px dashed #6c757d; height: 0; }

.link-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  z-index: 1;
  flex-shrink: 0;
}
.link-label {
  font-size: 10px;
  color: #666;
  z-index: 1;
  white-space: nowrap;
}

.toggle-hint {
  font-size: 10px;
  color: #aaa;
}

.cause-message {
  padding: 4px 12px;
  font-size: 11px;
  margin-top: 2px;
}
.cause-message.unstable { color: #856404; }
.cause-message.disconnected { color: #dc3545; }

.detail-panel {
  margin-top: 4px;
}
.detail-title {
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
  padding: 0 4px;
}
</style>
